import React, { useState, useMemo } from "react";
import { GALLERY } from "@/constants/testIds";
import { Ornament } from "@/components/Logo";
import ArtworkLightbox from "@/components/ArtworkLightbox";
import { trackArtworkClick, trackCtaClick } from "@/lib/analytics";

function currency(n, code = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    maximumFractionDigits: 0,
  }).format(n);
}

const CATEGORY_ORDER = [
  "All",
  "Large Pieces",
  "Celebrity Pieces",
  "Fine Art",
  "Assemblage Plastique",
  "Studio Work",
];

const PAGE_SIZE = 24;

export default function Gallery({ artworks, onReserve }) {
  const [zoomed, setZoomed] = useState(null);
  const [activeCat, setActiveCat] = useState("All");
  const [visible, setVisible] = useState(PAGE_SIZE);

  // Build the tabs based on which categories are actually present
  const categories = useMemo(() => {
    const present = new Set();
    (artworks || []).forEach((a) => a.category && present.add(a.category));
    return CATEGORY_ORDER.filter(
      (c) => c === "All" || present.has(c)
    ).concat(
      // include any long-tail categories not in the preferred order
      Array.from(present).filter((c) => !CATEGORY_ORDER.includes(c))
    );
  }, [artworks]);

  const filtered = useMemo(() => {
    if (activeCat === "All") return artworks;
    return (artworks || []).filter((a) => a.category === activeCat);
  }, [artworks, activeCat]);

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > shown.length;

  const switchCat = (c) => {
    setActiveCat(c);
    setVisible(PAGE_SIZE);
  };

  return (
    <section
      id="gallery"
      data-testid={GALLERY.root}
      className="relative bg-[var(--ivory)] py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.5em] uppercase text-[#722F37]">
            The Full Catalogue · {artworks?.length || 0} works
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight font-light">
            The Gallery
          </h2>
          <div className="mx-auto mt-6 w-40">
            <Ornament color="#D4AF37" />
          </div>
        </div>

        {/* Category filter tabs */}
        <div
          data-testid="gallery-filter-tabs"
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => switchCat(c)}
              className={`px-4 h-9 text-[10px] tracking-[0.3em] uppercase font-medium transition-colors ${
                activeCat === c
                  ? "bg-[#0B3B24] text-[var(--ivory)]"
                  : "bg-transparent text-[#1A1A1A]/60 hover:text-[#0B3B24] border border-[#0B3B24]/20 hover:border-[#0B3B24]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {shown.map((art, idx) => (
            <ArtworkTile
              key={art.id}
              art={art}
              index={idx}
              onZoom={() => {
                trackArtworkClick(art.id);
                setZoomed(art);
              }}
              onReserve={() => {
                trackArtworkClick(art.id);
                trackCtaClick(`gallery_reserve_${art.id}`);
                onReserve(art);
              }}
            />
          ))}
        </div>

        {hasMore && (
          <div className="mt-16 flex justify-center">
            <button
              data-testid="gallery-load-more"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="btn-outline-gold px-8 h-12 tracking-[0.25em] text-[11px] uppercase font-medium"
            >
              View {Math.min(PAGE_SIZE, filtered.length - shown.length)} more →
            </button>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="py-16 text-center text-[#1A1A1A]/50 font-serif italic">
            No works in this category yet.
          </div>
        )}
      </div>

      {zoomed && (
        <ArtworkLightbox
          art={zoomed}
          onClose={() => setZoomed(null)}
          onReserve={() => {
            const a = zoomed;
            setZoomed(null);
            onReserve(a);
          }}
        />
      )}
    </section>
  );
}

function ArtworkTile({ art, index, onZoom, onReserve }) {
  return (
    <figure
      data-testid={GALLERY.card(art.id)}
      className="group flex flex-col fade-in"
      style={{ animationDelay: `${Math.min(index, 12) * 60}ms` }}
    >
      <button
        onClick={onZoom}
        className="relative block overflow-hidden bg-black/5"
        aria-label={`View ${art.title}`}
      >
        <img
          data-testid={GALLERY.cardImage(art.id)}
          src={art.image_url}
          alt={art.title}
          className="w-full aspect-[4/5] object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
          loading="lazy"
        />
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-start p-6"
          style={{
            background:
              "linear-gradient(to top, rgba(11,59,36,0.72), transparent 60%)",
          }}
        >
          <span
            data-testid={GALLERY.viewBtn(art.id)}
            className="text-ivory text-[10px] tracking-[0.4em] uppercase"
            style={{ color: "var(--ivory)" }}
          >
            Zoom detail →
          </span>
        </div>
      </button>
      <figcaption className="mt-5 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h3
            data-testid={GALLERY.cardTitle(art.id)}
            className="font-serif text-2xl tracking-tight text-[#1A1A1A] truncate"
          >
            {art.title}
          </h3>
          {art.subtitle && (
            <div className="mt-1 font-serif italic text-sm text-[#1A1A1A]/60 truncate">
              {art.subtitle}
            </div>
          )}
          <div className="mt-2 text-[11px] tracking-[0.25em] uppercase text-[#1A1A1A]/50">
            {[art.medium, art.dimensions, art.year].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-serif text-lg text-[#722F37]">
            {currency(art.price, art.currency)}
          </div>
          {!art.is_available && (
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/50 mt-1">
              Reserved
            </div>
          )}
        </div>
      </figcaption>
      <button
        data-testid={GALLERY.reserveBtn(art.id)}
        onClick={onReserve}
        className="mt-5 btn-outline-gold h-11 px-5 tracking-[0.25em] text-[10px] uppercase font-medium"
      >
        Inquire / Reserve this piece
      </button>
    </figure>
  );
}
